import * as React from 'react';
import { styled } from '@mui/material/styles';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import { dataConstants } from '../../constants/index.js';
import { getRandomStr } from '../../helpers/index.js';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { Box, Chip, Grid, IconButton, Paper } from '@mui/material';
import TextField from '@mui/material/TextField';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { levenshtein } from '../../utils/levenshtein/index.js';
import { useRouter } from "next/router";

const { INDUSTRY: industries, SECTOR_OPTIONS } = dataConstants;
const sectors = SECTOR_OPTIONS?.sort((a, b) => a.shortName.localeCompare(b.shortName, undefined, { sensitivity: 'base' }))

const popIndustries = [
  { "sector": "Financial Services", "shortName": "Asset Management" },
  { sector: "Technology", shortName: "Consumer Electronics" },
  // { sector: "Technology", shortName: "Scientific & Technical Instruments", },
  { sector: "Consumer Cyclical", shortName: "Gambling" },
  { sector: "Industrials", shortName: "Marine Shipping" },
  { sector: "Financial Services", shortName: "Credit Services" },
];

const ListItem = styled('li')(({ theme }) => ({
  margin: '4px 8px 4px 0',
}));

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    // color: theme.palette.primary.main,
    color: "#2a2d64",
  },
  [`& .${tooltipClasses.tooltip}`]: {
    // backgroundColor: theme.palette.primary.main,
    backgroundColor: "#2a2d64",
    fontWeight: 600,
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    padding: '5px 15px',
  },
}));

const QuickSearchField = styled((props) => (
  <TextField
    {...props}
  />
))(({ theme }) => ({
  [theme.breakpoints.down('xs')]: {
    width: '100%',
  },
  margin: '0 12px 4px 12px',
  '& .MuiSvgIcon-root': {
    marginRight: '4px',
    color: 'hsl(235deg 59.62% 21.27%)' /*#020B4A*/
  },
  '& .MuiInputBase-input-MuiInput-input:hover': {
    background: '#f0f1f4',
  },
  border: `1px solid hsla(216, 41%, 87%, 0.8)`,
  borderRadius: '7px',
  // minWidth: theme.breakpoints.down('xs') ? '17rem' : '100%',
  [theme.breakpoints.down('md')]: {
    minWidth: '14rem',
  },
  [theme.breakpoints.up('md')]: {
    minWidth: '17rem',
  },
  [theme.breakpoints.up('lg')]: {
    minWidth: '20.92rem',
  },
  '& .Mui-focused': {
    background: '#fff',
    borderRadius: '7px',
  },
  '& .MuiInput-underline:before': {
    padding: '4px',
    border: `0 !important`,
  },
  '& .MuiInput-underline:after': {
    // border: `1px solid ${theme.palette.text.link}`,
    border: `0 !important`,
  },
  '& .MuiInputBase-input-MuiInput-input': {
    fontSize: '.8rem !important',
  },
  '& .MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled):before': {
    background: '#fff',
  }
}));

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  // border: `1px solid ${theme.palette.divider}`,
  background: '#fbfbfc',
  border: '1px solid rgb(229, 234, 242)',
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:first-child': {
    borderRadius: '7px 7px 0 0',
  },
  '&:last-child': {
    borderRadius: '0 0 7px 7px',
  },
  '&:last-child .MuiAccordionDetails-root': {
    borderRadius: '0 0 7px 7px',
  },
  '& .MuiAccordionDetails-root': {
    padding: '12px',
    background: '#fff'
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosRoundedIcon sx={{ fontSize: '0.9rem', color: '#2d4156' }} />}
    startIcon={<CloseRoundedIcon sx={{ color: 'rgba(0, 0, 0, 0.26) !important' }} />}
    {...props}
  />
))(({ theme }) => ({
  color: '#2d4156',
  minHeight: "37px",
  fontSize: '.875rem',
  // backgroundColor:
  //   theme.palette.mode === 'dark'
  //     ? 'rgba(255, 255, 255, .05)'
  //     : '#fbfbfc',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1),
  },

}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgb(229, 234, 242)',
}));

const findClosestMatch = (query) => {
  const closestMatch = industries.reduce((closest, item) => {
    const sectorDistance = levenshtein(query, item.sector.toLowerCase());
    const shortNameDistance = levenshtein(query, item.shortName.toLowerCase());

    if (sectorDistance < closest.distance || shortNameDistance < closest.distance) {
      return {
        distance: Math.min(sectorDistance, shortNameDistance),
        item,
      };
    }

    return closest;
  }, { distance: Number.MAX_SAFE_INTEGER, item: null });

  return closestMatch.item;
}

const IndustriesAccordion = ({ industryName, popularIndustries, subTab, sx, onIndustryClick }) => {
  const router = useRouter();
  const [filteredIndustries, setFilteredIndustries] = React.useState(industries);
  const [expanded, setExpanded] = React.useState('basic_materials');
  const [selectedIndustries, setSelectedIndustries] = React.useState(popularIndustries ?? popIndustries);
  const [searchText, setSearchText] = React.useState('');
  const [searchTextErr, setSearchTextErr] = React.useState('');
  const [suggestion, setSuggestion] = React.useState(null);

  // const handleSearch = (e) => {
  //   const query = e.target.value.toLowerCase();
  //   setSearchText(query);

  //   const filteredItems = industries.filter(item =>
  //     item.sector.toLowerCase().includes(query) ||
  //     item.shortName.toLowerCase().includes(query)
  //   );

  //   setFilteredIndustries(filteredItems);
  // };


  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchText(query);

    if (query.length === 0) {
      setSuggestion(null);
      setFilteredIndustries(industries);
      return;
    }

    const filteredItems = industries.filter(item =>
      item.sector.toLowerCase().includes(query) ||
      item.shortName.toLowerCase().includes(query)
    );

    if (filteredItems.length === 0) {
      // No exact match found, try to find the closest match
      const closestMatch = findClosestMatch(query);
      setSuggestion(closestMatch);
      setSearchTextErr(`Your search - <b>${e?.target?.value}</b> - did not match any industry. Search instead for <i><b><a href="/sectors/industry/${closestMatch?.shortName?.replace(/&/g, 'and').replace(/\s/g, '-')?.toLowerCase()}">${closestMatch?.shortName}</a></b></i>`)
    } else {
      setSuggestion(null);
    }

    setFilteredIndustries(filteredItems.length > 0 ? filteredItems : industries);
  };

  const clearSearch = () => {
    setSearchText('');
    setSuggestion(null);
    setFilteredIndustries(industries);
  };

  React.useEffect(() => {
    const uniqueSectors = Array.from(new Set(filteredIndustries.map(item => item.sector))).sort();
    setExpanded(uniqueSectors[0]?.toLowerCase()?.replace(/ /g, '_'))
  }, [filteredIndustries])

  // React.useEffect(() => {
  //   setFilteredIndustries(industries)
  //   setSelectedIndustries(popularIndustries ?? popIndustries)
  // }, [])

  const handleAccordion = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  // const handleDelete = (chipToDelete) => () => {
  //   setSelectedIndustries((chips) => chips.filter((chip) => chip.shortName !== chipToDelete.shortName));
  //   setFormula([
  //     {
  //       andor: "AND",
  //       metricsCriteria: selectedIndustries?.filter((chip) => chip.shortName !== chipToDelete.shortName)?.map((item) => ({
  //         code: `\`industry\``,
  //         condition: "EQUALS",
  //         value: item.shortName,
  //       })),
  //     },
  //   ])
  // };

  // const handleInsert = (chipToInsert) => () => {
  //   const newChip = { sector: chipToInsert?.sector, shortName: chipToInsert?.shortName };
  //   const newChips = [...selectedIndustries, newChip]
  //   setSelectedIndustries((chips) => [...chips, newChip]);
  //   setFormula([
  //     {
  //       andor: "AND",
  //       metricsCriteria: newChips?.map((item) => ({
  //         code: `\`industry\``,
  //         condition: "EQUALS",
  //         value: item.shortName,
  //       })),
  //     },
  //   ])
  // };

  return (
    <>
      {subTab ? null :
        <Grid size={{ xs: 12, sm: 12, md: 12 }} sx={{ ...sx }}>
          <Paper
            sx={{
              display: 'flex',
              alignItems: 'center',
              // justifyContent: 'center',
              flexWrap: 'wrap',
              listStyle: 'none',
              boxShadow: 0,
              pt: 1, px: 0, mb: 0, pb: searchTextErr !== '' && suggestion ? 0 : .7,
            }}
            component="ul"
          >
            <QuickSearchField
              variant="standard"
              value={searchText}
              onChange={handleSearch}
              placeholder="Search through sectors or industries"
              className="searchField"
              InputProps={{
                startAdornment: <SearchIcon fontSize="small" sx={{ ml: 1 }} />,
                endAdornment: (
                  <IconButton
                    title="Clear"
                    aria-label="Clear"
                    size="small"
                    style={{ visibility: searchText ? 'visible' : 'hidden' }}
                    onClick={clearSearch}
                    sx={{ '& .MuiSvgIcon-fontSizeSmall': { marginRight: '0 !important' } }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              sx={{
                // mr: 2,
                // mb: 2,
                background: '#fff',
                '&.MuiTextField-root.searchField': {
                  m: '0px 12px 0px 0px',
                },
                '& .MuiSvgIcon-root': {
                  fontSize: '1.1rem',
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '.87rem',
                },
                '& .MuiInputBase-input': {
                  fontSize: '.87rem',
                  padding: '7px'
                },
              }}
            />
            {selectedIndustries?.find(d => d.shortName === industryName) ? null :
              <ListItem>
                <Chip
                  label={industryName}
                  // component="a"
                  onClick={() => { router.push(`/sectors/industry/${industryName?.replace(/&/g, 'and').replace(/\s/g, '-')?.toLowerCase()}`); onIndustryClick() }}
                  // clickable
                  // onDelete={handleDelete(d)}
                  deleteIcon={<CloseRoundedIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.26) !important', fontSize: '1.1rem !important', }} />}
                  sx={{
                    background: 'rgb(180 186 193 / 12%) !important',
                    // backgroundImage:'linear-gradient(45deg,#dbe8f0,#e3ebf1,#f8f9fa),linear-gradient(45deg,#022f77,rgb(2, 11, 74),#3d83d7)',
                    border: '1px solid hsla(216, 41%, 87%, 0.8)',
                    borderRadius: "5px",
                    '& .MuiChip-label': { color: "#174ea6" }
                  }}

                />
              </ListItem>}
            {selectedIndustries.map((d) => {
              return (
                <ListItem key={getRandomStr(5)}>
                  <Chip
                    label={d.shortName}
                    // component="a"
                    onClick={() => { router.push(`/sectors/industry/${d?.shortName?.replace(/&/g, 'and').replace(/\s/g, '-')?.toLowerCase()}`); onIndustryClick() }}
                    // clickable
                    // onDelete={handleDelete(d)}
                    deleteIcon={<CloseRoundedIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.26) !important', fontSize: '1.1rem !important', }} />}
                    sx={{
                      '&:hover': { background: 'rgb(180 186 193 / 12%) !important' },
                      '&:hover .MuiChip-label': { color: "#174ea6" },
                      background: industryName === d.shortName ? 'rgb(180 186 193 / 12%) !important' : '#fbfbfc !important',
                      // backgroundImage:'linear-gradient(45deg,#dbe8f0,#e3ebf1,#f8f9fa),linear-gradient(45deg,#022f77,rgb(2, 11, 74),#3d83d7)',
                      border: '1px solid hsla(216, 41%, 87%, 0.8)',
                      borderRadius: "5px",
                      '& .MuiChip-label': { color: industryName === d?.shortName ? "#174ea6" : 'rgb(67, 65, 102)', }
                    }}

                  />
                </ListItem>
              );
            })}
          </Paper>
        </Grid>}
      {searchTextErr !== '' && suggestion && !subTab &&
        <Grid size={{ xs: 12, sm: 12, md: 12 }}>
          <Typography variant="body1" sx={{ fontSize: '.875rem', px: .5 }} dangerouslySetInnerHTML={{ __html: searchTextErr }} />
        </Grid>}
      <Grid size={{ xs: 12, sm: 4, md: 2.8 }}
        sx={{
          '& .MuiTextField-root.searchField': {
            minWidth: '100% !important',
            mt: '16px !important',
            mb: '14px !important',
          },
          ...sx
        }}>
        {subTab ?
          <QuickSearchField
            subTab={subTab}
            variant="standard"
            value={searchText}
            onChange={handleSearch}
            placeholder="Search through sectors or industries"
            className="searchField"
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ ml: 1 }} />,
              endAdornment: (
                <IconButton
                  title="Clear"
                  aria-label="Clear"
                  size="small"
                  style={{ visibility: searchText ? 'visible' : 'hidden' }}
                  onClick={clearSearch}
                  sx={{ '& .MuiSvgIcon-fontSizeSmall': { marginRight: '0 !important' } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              ),
            }}
            sx={{
              // mr: 2,
              // mb: 2,
              background: '#fff',
              '&.MuiTextField-root.searchField': {
                m: '0px 12px 0px 0px',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.1rem',
              },
              '& .MuiInputBase-input::placeholder': {
                fontSize: '.87rem',
              },
              '& .MuiInputBase-input': {
                fontSize: '.87rem',
                padding: '7px'
              },
            }}
          /> : null}
        {searchTextErr !== '' && suggestion &&
          <Typography variant="body1" component="div" sx={{ fontSize: '.875rem', px: .5, mb: 1.5, }} dangerouslySetInnerHTML={{ __html: searchTextErr }} />}
        <Box component="div" className="industry-wrapper" sx={{}}>
          {sectors?.map((d) => {
            const panel = d?.shortName?.toLowerCase()?.replace(/ /g, '_')
            const _industries = filteredIndustries?.filter((i) => i.sector === d?.shortName)?.sort((a, b) => a.shortName.localeCompare(b.shortName, undefined, { sensitivity: 'base' }))
            // const available = selectedIndustries?.filter(i => i?.sector === d?.legend)
            return (_industries && _industries.length > 0 ?
              <Accordion expanded={expanded === panel} onChange={handleAccordion(panel)}>
                <AccordionSummary aria-controls={`${panel}-content`} id={`${panel}-header`}>
                  {d?.legend}
                  {/* {available.length > 0 ? <div style={{ marginLeft: 'auto', color: 'rgb(0 0 0 / 32%)', fontWeight: 600 }}>{available.length}</div> : null} */}
                </AccordionSummary>
                <AccordionDetails>
                  {_industries?.map(d => {
                    // const available = selectedIndustries?.find(i => i?.shortName === d?.shortName)
                    return <LightTooltip key={getRandomStr(5)} title={d?.description} placement="left" disableInteractive>
                      <Chip
                        // component="a"
                        onClick={() => { router.push(`/sectors/industry/${d?.shortName?.replace(/&/g, 'and').replace(/\s/g, '-')?.toLowerCase()}`); onIndustryClick() }}
                        // clickable
                        // size="small"
                        variant="outlined"
                        label={d?.shortName}
                        // onClick={available ? handleDelete(d) : handleInsert(d)}
                        // onDelete={available ? handleDelete(d) : undefined}
                        deleteIcon={<CloseRoundedIcon fontSize="small" sx={{ color: 'rgba(0, 0, 0, 0.26) !important', fontSize: '1.1rem !important' }} />}
                        sx={{
                          '&:hover': { background: 'rgb(180 186 193 / 37%) !important' },
                          '&:hover .MuiChip-label': { color: "rgba(0, 0, 0, 0.87)" },
                          m: .45, borderRadius: '5px', border: '1px solid hsla(216, 41%, 87%, 0.8)', fontSize: '.8rem', height: "26px",
                          bgcolor: industryName === d?.shortName ? 'rgb(180 186 193 / 37%)!important' : '', '& .MuiChip-label': { color: industryName === d?.shortName ? "rgba(0, 0, 0, 0.87)" : 'rgb(67, 65, 102)', px: 1 }
                        }}
                      />
                    </LightTooltip>
                  })}
                  {/* <Divider sx={{ my: 1, borderColor: 'rgba(208, 219, 235, 0.8)' }} /> */}
                  {/* #2d4156 */}
                  {/* <Typography variant="caption" color="text.secondary">
                    {d?.description}
                  </Typography> */}
                </AccordionDetails>
              </Accordion> : null);
          })}
        </Box>
      </Grid>
    </>);
}

export default IndustriesAccordion
